import { Component, type ReactNode } from 'react'

interface Props { children: ReactNode }
interface State { errored: boolean }

/**
 * Catches "Failed to fetch dynamically imported module" errors that happen
 * when a new deployment replaces the JS chunks the browser cached in the old
 * index.html.  On first hit it reloads the page once (which fetches the fresh
 * index.html and the correct chunk hashes).  If reloading didn't help it
 * shows a simple prompt so the user isn't stuck on a blank screen.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { errored: false }

  static getDerivedStateFromError(): State {
    return { errored: true }
  }

  componentDidCatch(error: unknown) {
    const msg = error instanceof Error ? error.message : String(error)
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module')

    if (isChunkError) {
      // Only reload once — guard with sessionStorage to avoid infinite loop.
      const reloaded = sessionStorage.getItem('chunk_reload')
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1')
        window.location.reload()
        return
      }
    }
  }

  render() {
    if (this.state.errored) {
      return (
        <div className="flex flex-col items-center justify-center h-full py-24 gap-4 text-gray-500 text-sm">
          <p>A new version of DiamondOps was deployed.</p>
          <button
            onClick={() => { sessionStorage.removeItem('chunk_reload'); window.location.reload() }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            Reload to update
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

